import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import CryptoJS from "crypto-js";
import type { User, Admin } from "@/lib/supabase";

interface AuthUser extends User {
  type: "user";
}

interface AuthAdmin extends Admin {
  type: "admin";
}

type AuthContextType = {
  user: AuthUser | AuthAdmin | null;
  isAuthenticated: boolean;
  login: (user: AuthUser | AuthAdmin) => void;
  logout: () => void;
  isLoading: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Read AES key from env
const SECRET_KEY = import.meta.env.VITE_SECRET_KEY || "";

// Safe Encrypt
const encrypt = (data: string) => {
  try {
    if (!SECRET_KEY) {
      console.warn("⚠️ SECRET_KEY missing in environment. Using fallback encryption.");
      // Fallback: simple base64 encoding if SECRET_KEY is missing
      return btoa(encodeURIComponent(data));
    }
    return CryptoJS.AES.encrypt(data, SECRET_KEY).toString();
  } catch (err) {
    console.error("Encrypt error:", err);
    // Fallback: simple base64 encoding
    try {
      return btoa(encodeURIComponent(data));
    } catch {
      return data;
    }
  }
};

// Safe Decrypt
const decrypt = (cipher: string) => {
  try {
    if (!SECRET_KEY) {
      console.warn("⚠️ SECRET_KEY missing in environment. Using fallback decryption.");
      // Fallback: try base64 decoding if SECRET_KEY is missing
      try {
        return decodeURIComponent(atob(cipher));
      } catch {
        return null;
      }
    }
    const bytes = CryptoJS.AES.decrypt(cipher, SECRET_KEY);
    const result = bytes.toString(CryptoJS.enc.Utf8);
    if (!result) {
      // Try fallback base64 decoding if AES decrypt fails
      try {
        return decodeURIComponent(atob(cipher));
      } catch {
        return null;
      }
    }
    return result || null;
  } catch (err) {
    console.error("Decrypt error:", err);
    // Fallback: try base64 decoding
    try {
      return decodeURIComponent(atob(cipher));
    } catch {
      return null;
    }
  }
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<AuthUser | AuthAdmin | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkCookieAuth = () => {
      try {
        const encryptedCookie = Cookies.get("authId");

        if (encryptedCookie) {
          const decrypted = decrypt(encryptedCookie);

          if (decrypted) {
            const storedUser: AuthUser | AuthAdmin = {
              id: decrypted,
              type: "user",
            } as any;

            setUser(storedUser);
            console.log(`✅ Session restored for ID: ${decrypted}`);
          }
        }
      } catch (error) {
        console.error("❌ Error restoring auth session:", error);
        Cookies.remove("authId");
      } finally {
        setIsLoading(false);
      }
    };

    checkCookieAuth();
  }, []);

  const login = (userData: AuthUser | AuthAdmin) => {
    setUser(userData);

    const encryptedId = encrypt(userData.id);

    Cookies.set("authId", encryptedId, {
      expires: 7,
      secure: true,
      sameSite: "Strict",
    });

    console.log(`✅ ${userData.type} logged in successfully`);

    if (userData.type === "admin") navigate("/admin-dashboard");
    else navigate("/user-dashboard");
  };

  const logout = () => {
    const userType = user?.type;
    setUser(null);
    Cookies.remove("authId");

    console.log(`👋 ${userType} logged out`);

    navigate("/");
  };

  const value = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
